import { cn } from "@/lib/cn";
import { Slot } from "@radix-ui/react-slot";
import { type LinkProps, Link as RouterLink } from "@tanstack/react-router";
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ReactNode,
  forwardRef,
  useMemo,
} from "react";
import { Fragment } from "react/jsx-runtime";
import Icon from "../Icon/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Types
type BreadcrumbProps = { link?: LinkProps; label: string };
type BreadcrumbPart =
  | { type: "normal"; part: BreadcrumbProps }
  | { type: "trunc"; part: BreadcrumbProps[] };

export type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbProps[];
  /**
   * The maximum number of breadcrumbs to show
   * @default 3
   */
  maxVisible?: number;
};

// Internal Components
const Nav = forwardRef<
  HTMLElement,
  ComponentPropsWithoutRef<"nav"> & {
    separator?: ReactNode;
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />);
Nav.displayName = "Nav";

const List = forwardRef<HTMLOListElement, ComponentPropsWithoutRef<"ol">>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      className={cn(
        "flex flex-wrap items-center gap-1.5 break-words text-sm text-neutral-muted-on-fill sm:gap-2.5",
        className,
      )}
      {...props}
    />
  ),
);
List.displayName = "List";

const Item = forwardRef<HTMLLIElement, ComponentPropsWithoutRef<"li">>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    />
  ),
);
Item.displayName = "Item";

const BreadcrumbLink = forwardRef<
  HTMLAnchorElement,
  ComponentPropsWithoutRef<"a"> & {
    asChild?: boolean;
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";
  return (
    <Comp
      ref={ref}
      className={cn("transition-colors hover:text-foreground", className)}
      {...props}
    />
  );
});
BreadcrumbLink.displayName = "BreadcrumbLink";

const Page = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("font-normal text-neutral-muted-on-fill", className)}
      {...props}
    />
  ),
);
Page.displayName = "Page";

const Separator = ({ children, className, ...props }: ComponentProps<"li">) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn("[&>svg]:size-3.5", className)}
    {...props}
  >
    {children ?? <Icon iconName="ChevronRight" />}
  </li>
);
Separator.displayName = "Separator";

const Ellipsis = ({ className, ...props }: ComponentProps<"span">) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <Icon iconName="MoreHorizontalIcon" />
    <span className="sr-only">More</span>
  </span>
);
Ellipsis.displayName = "Ellipsis";

function BreadcrumbsSingle({ breadcrumb }: { breadcrumb: BreadcrumbProps }) {
  return breadcrumb.link ? (
    <BreadcrumbLink asChild>
      <RouterLink {...breadcrumb.link}>{breadcrumb.label}</RouterLink>
    </BreadcrumbLink>
  ) : (
    <Page>{breadcrumb.label}</Page>
  );
}

function BreadcrumbsTruncated({
  breadcrumbs,
}: {
  breadcrumbs: BreadcrumbProps[];
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Ellipsis className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {breadcrumbs.map((bc, index) => (
          <DropdownMenuItem key={index}>
            <RouterLink {...bc.link}>{bc.label}</RouterLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Main Component
const Breadcrumb = ({ breadcrumbs, maxVisible = 3 }: BreadcrumbsProps) => {
  const parts = useMemo<BreadcrumbPart[]>(() => {
    if (breadcrumbs.length <= maxVisible) {
      return breadcrumbs.map((bc) => ({ type: "normal", part: bc }));
    }

    const lastBreadcrumb = breadcrumbs.at(-1);
    if (!lastBreadcrumb) {
      return breadcrumbs.map((bc) => ({ type: "normal", part: bc }));
    }

    return [
      { type: "normal", part: breadcrumbs[0] },
      { type: "trunc", part: breadcrumbs.slice(1, -1) },
      { type: "normal", part: lastBreadcrumb },
    ];
  }, [breadcrumbs, maxVisible]);

  return (
    <Nav>
      <List>
        {parts.map((part, index) => (
          <Fragment key={index}>
            <Item>
              {part.type === "normal" ? (
                <BreadcrumbsSingle breadcrumb={part.part} />
              ) : (
                <BreadcrumbsTruncated breadcrumbs={part.part} />
              )}
            </Item>
            {index < parts.length - 1 && <Separator />}
          </Fragment>
        ))}
        <Icon iconName="ChevronRight" className="size-3.5" />
      </List>
    </Nav>
  );
};

Breadcrumb.displayName = "Breadcrumb";

export { Breadcrumb, BreadcrumbProps };
